import { memo, useState, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useConnectionStore } from "stores/ConnectionStore";
import { useFlairStore } from "stores/FlairStore";
import {
  OAuth2Progress,
  OAuth2URLSuffix,
  useOAuth2Store,
} from "stores/OAuth2Store";

import { getServicesByName, Service } from "utility/connections";
import { GenericObject } from "utility/misc";
import { notifyError, notifySuccess } from "utility/notify";

import ExternalSources from "external/sources";

import LoaderModal from "layouts/components/LoaderModal";

// -----------------------------------------------------------------------------

const OAuthSourceToURLSuffix: GenericObject = {
  azure: OAuth2URLSuffix.AZURE,
  box: OAuth2URLSuffix.BOX,
  github: OAuth2URLSuffix.GITHUB,
  google: OAuth2URLSuffix.GOOGLE,
  m365: OAuth2URLSuffix.MICROSOFT_365,
};

enum Phase {
  OAuthProgress,
  Error,
  Done,
}

// -----------------------------------------------------------------------------

const EnvironmentSourcesOAuth = memo(
  function EnvironmentSourcesOAuth(): JSX.Element {
    const [oauthStore, oauthActions] = useOAuth2Store();
    const [connectionStore] = useConnectionStore();
    const [, flairActions] = useFlairStore();
    const [phase, setPhase] = useState(Phase.OAuthProgress);

    const searchParams = useSearchParams()[0];
    const service = searchParams.get("service") ?? "none";

    const [sourcesByName, setSourcesByName] = useState<Map<string, Service>>(
      new Map<string, Service>()
    );

    useEffect(() => {
      setSourcesByName(getServicesByName(ExternalSources.services));
    }, [setSourcesByName]);

    //
    useEffect(() => {
      switch (oauthStore.progress) {
        case OAuth2Progress.Unknown: {
          const code = searchParams.get("code");
          const state = searchParams.get("state");
          const params = getParams(service, searchParams);

          oauthActions.submitParams(
            OAuthSourceToURLSuffix[service],
            code,
            state,
            params
          );
          break;
        }

        case OAuth2Progress.Success: {
          // celebration on first connection
          if (Object.keys(connectionStore.connections).length < 1) {
            flairActions.startCelebration();
          }

          notifySuccess(
            `Connected to ${sourcesByName.get(service)?.label ?? "Unknown"}`
          );
          setPhase(Phase.Done);
          break;
        }

        case OAuth2Progress.Error:
        case OAuth2Progress.ErrorOAuthParam: {
          setPhase(Phase.Error);
          notifyError(
            `Failed connecting to ${
              sourcesByName.get(service)?.label ?? "Unknown"
            }`
          );
          break;
        }
      }
    }, [
      oauthStore.progress,
      oauthActions,
      connectionStore.connections,
      service,
      searchParams,
      flairActions,
      sourcesByName,
    ]);

    return (
      <>
        {phase === Phase.OAuthProgress && <LoaderModal />}
        {phase === Phase.Error && (
          <>
            <Alert severity="error" sx={{ flexGrow: 0 }}>
              {oauthStore.errorDetail}
            </Alert>
            <Box sx={{ width: "100%", height: "100%", mt: 2 }}>
              <Typography variant="h5">
                There was an error connecting to the source
              </Typography>
              <Button component={Link} to="/environment/sources">
                Go back
              </Button>
            </Box>
          </>
        )}
        {phase === Phase.Done && <Navigate to="/environment/sources" />}
      </>
    );
  }
);

export default EnvironmentSourcesOAuth;

// -----------------------------------------------------------------------------

function getParams(
  source: string,
  searchParams: URLSearchParams
): GenericObject | undefined {
  switch (source) {
    case "github":
      return {
        installation_id: searchParams.get("installation_id") ?? null,
      };
  }
}
